import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckIcon,
  DotsSixVerticalIcon,
  FolderPlusIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { Fragment, useMemo, useState } from "react";
import { Field } from "@/components/molecules/field";
import { differs, FormFooter } from "@/components/molecules/form-footer";
import { SettingsSection } from "@/components/molecules/settings-section";
import { useTranslation } from "@/features/utils/i18n";
import type {
  QualificationForm,
  CreateFormRequest,
  UpdateFormRequest,
  Question,
  QuestionType,
  ScoringMode,
  ScoringStrategy,
} from "@/features/qualifications/types";
import {
  groupTitles,
  orderByGroup,
} from "@/features/qualifications/utils/groups";
import { QuestionCard } from "./question-card";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const FORM_ID = "qualification-form-editor";

const newQuestion = (group?: string): Question => ({
  id: crypto.randomUUID(),
  ...(group ? { group } : {}),
  type: "boolean",
  label: "",
  required: true,
  scoring_mode: "weight",
  scoring: {
    default: 0,
    weight: 1,
  },
});

interface FormEditorProps {
  initialData?: QualificationForm | Partial<CreateFormRequest>;
  onSubmit: (data: CreateFormRequest | UpdateFormRequest) => Promise<void>;
  /** A form not saved yet: its save bar is up from the start. */
  isNew?: boolean;
  saving?: boolean;
}

/**
 * The editor of a qualification form, laid out as the settings pages are:
 * the form's identity, its decision thresholds, then its questions, each a
 * `SettingsSection`; the save bar is `FormFooter`, which also asks before
 * leaving with unsaved edits.
 *
 * Its buttons used to sit at the bottom of an 800-line form, invisible while
 * editing the first question. A new form — from a template, from AI or from
 * scratch — has nothing saved yet, so its bar is up from the start.
 */
export const FormEditor = ({
  initialData,
  onSubmit,
  isNew = false,
  saving = false,
}: FormEditorProps) => {
  const { t } = useTranslation("qualifications");
  const initial = useMemo(
    () => ({
      name: initialData?.name || "",
      description: initialData?.description || "",
      status: (initialData as QualificationForm)?.status || "draft",
      minToApprove: initialData?.auto_accept_threshold?.toString() || "",
      maxToReject: initialData?.auto_reject_threshold?.toString() || "",
      questions: orderByGroup(
        initialData?.questions?.questions || [newQuestion()],
      ),
    }),
    [initialData],
  );
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [status, setStatus] = useState<"active" | "inactive" | "draft">(
    initial.status,
  );
  const [scoringStrategy] = useState<ScoringStrategy>("weighted");
  const [minToApprove, setMinToApprove] = useState(initial.minToApprove);
  const [maxToReject, setMaxToReject] = useState(initial.maxToReject);
  const [questions, setQuestions] = useState<Question[]>(initial.questions);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null,
  );

  const dirty =
    isNew ||
    differs(
      { name, description, status, minToApprove, maxToReject, questions },
      initial,
    );

  const reset = () => {
    setName(initial.name);
    setDescription(initial.description);
    setStatus(initial.status);
    setMinToApprove(initial.minToApprove);
    setMaxToReject(initial.maxToReject);
    setQuestions(initial.questions);
    setEditingQuestionId(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // A question dropped among another group's joins it: dragging is how a question
  // is moved from one section to another.
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setQuestions((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const group = items[newIndex].group;

        return arrayMove(items, oldIndex, newIndex).map((item) =>
          item.id === active.id ? withGroup(item, group) : item,
        );
      });
    }
  };

  const groups = groupTitles(questions);

  const addQuestion = () => {
    const question = newQuestion(questions[questions.length - 1]?.group);
    setQuestions([...questions, question]);
    setEditingQuestionId(question.id);
  };

  const addGroup = () => {
    let title = t("forms.editor.groups.newGroup");
    for (let n = 2; groups.includes(title); n++) {
      title = `${t("forms.editor.groups.newGroup")} ${n}`;
    }
    const question = newQuestion(title);
    setQuestions([...questions, question]);
    setEditingQuestionId(question.id);
  };

  /** Renames a group: every question under the old title takes the new one. */
  const renameGroup = (from: string, to: string) => {
    setQuestions(
      questions.map((q) =>
        q.group?.trim() === from ? { ...q, group: to } : q,
      ),
    );
  };

  const removeQuestion = (id: string) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    setQuestions(
      questions.map((q) => (q.id === id ? { ...q, ...updates } : q)),
    );
  };

  // A question moved to another group by its field joins that group's block once
  // its editing is done — not on every keystroke, or the card would jump away.
  const doneEditing = () => {
    setEditingQuestionId(null);
    setQuestions((items) => orderByGroup(items));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data: CreateFormRequest | UpdateFormRequest = {
      name,
      description: description || undefined,
      status,
      questions: {
        questions: orderByGroup(
          questions.map((q) => withGroup(q, q.group?.trim())),
        ),
      },
      scoring_strategy: scoringStrategy,
      // null, not undefined: an emptied field must clear the stored threshold,
      // and undefined is dropped from the JSON.
      auto_accept_threshold: minToApprove ? Number(minToApprove) : null,
      auto_reject_threshold: maxToReject ? Number(maxToReject) : null,
    };

    await onSubmit(data);
  };

  return (
    <>
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <SettingsSection
          title={t("forms.editor.general.title")}
          description={t("forms.editor.general.description")}
        >
          <Field label={t("forms.editor.name")} htmlFor="name" required>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("forms.editor.namePlaceholder")}
              required
            />
          </Field>

          <Field
            label={t("forms.editor.descriptionLabel")}
            htmlFor="description"
          >
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("forms.editor.descriptionPlaceholder")}
              rows={2}
            />
          </Field>

          <Field label={t("forms.editor.status")} htmlFor="status" required>
            <Select
              value={status}
              onValueChange={(v) =>
                setStatus(v as "active" | "inactive" | "draft")
              }
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(["draft", "active", "inactive"] as const).map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`forms.status.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </SettingsSection>

        <SettingsSection
          title={t("forms.editor.thresholds.title")}
          description={t("forms.editor.thresholds.description")}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("forms.editor.autoApprove")} htmlFor="minApprove">
              <Input
                id="minApprove"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={minToApprove}
                onChange={(e) => setMinToApprove(e.target.value)}
                placeholder={t("forms.editor.thresholdPlaceholder", {
                  value: 0.8,
                })}
              />
            </Field>
            <Field label={t("forms.editor.autoReject")} htmlFor="maxReject">
              <Input
                id="maxReject"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={maxToReject}
                onChange={(e) => setMaxToReject(e.target.value)}
                placeholder={t("forms.editor.thresholdPlaceholder", {
                  value: 0.5,
                })}
              />
            </Field>
          </div>
        </SettingsSection>

        {/* Stacked: a question card needs the whole width. */}
        <SettingsSection
          stacked
          title={t("forms.editor.questions.title")}
          description={t("forms.editor.questions.description")}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={questions.map((q) => q.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {questions.map((question, index) => {
                  const title = question.group?.trim() || "";
                  const previous = questions[index - 1]?.group?.trim() || "";
                  const opensSection =
                    groups.length > 0 && (index === 0 || title !== previous);
                  return (
                    <Fragment key={question.id}>
                      {opensSection && (
                        <GroupHeader
                          key={title}
                          title={title}
                          onRename={(to) => renameGroup(title, to)}
                        />
                      )}
                      <SortableQuestionWrapper
                        question={question}
                        index={index}
                        groups={groups}
                        isEditing={editingQuestionId === question.id}
                        onEdit={() => setEditingQuestionId(question.id)}
                        onUpdate={(updates) =>
                          updateQuestion(question.id, updates)
                        }
                        onRemove={() => removeQuestion(question.id)}
                        onDone={doneEditing}
                        canRemove={questions.length > 1}
                      />
                    </Fragment>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addQuestion}
            >
              <PlusIcon className="size-4" />
              {t("forms.editor.addQuestion")}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={addGroup}>
              <FolderPlusIcon className="size-4" />
              {t("forms.editor.groups.add")}
            </Button>
          </div>
        </SettingsSection>
      </form>

      <FormFooter
        form={FORM_ID}
        dirty={dirty}
        saving={saving}
        canSave={!!name.trim()}
        onSave={() => undefined}
        onCancel={isNew ? undefined : reset}
        saveLabel={isNew ? t("forms.createPage.submit") : undefined}
      />
    </>
  );
};

/** A question with its group set — or none, for an empty title. */
const withGroup = (question: Question, group?: string): Question => {
  const rest = { ...question };
  delete rest.group;
  return group ? { ...rest, group } : rest;
};

/**
 * The title over a section of questions, edited in place: renaming it renames the
 * group of every question in it, once the field is left (an emptied title is put
 * back — ungrouping is done question by question). The ungrouped questions get a
 * plain label.
 */
const GroupHeader = ({
  title,
  onRename,
}: {
  title: string;
  onRename: (title: string) => void;
}) => {
  const { t } = useTranslation("qualifications");
  const [draft, setDraft] = useState(title);
  if (!title) {
    return (
      <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("forms.editor.groups.ungrouped")}
      </p>
    );
  }
  const commit = () => {
    const next = draft.trim();
    if (next && next !== title) onRename(next);
    else setDraft(title);
  };
  return (
    <div className="pt-2">
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        aria-label={t("forms.editor.groups.title")}
        className="h-8 border-transparent px-1 text-sm font-semibold uppercase tracking-wide shadow-none hover:border-input focus-visible:border-input"
      />
    </div>
  );
};

interface SortableQuestionWrapperProps {
  question: Question;
  index: number;
  groups: string[];
  isEditing: boolean;
  onEdit: () => void;
  onUpdate: (updates: Partial<Question>) => void;
  onRemove: () => void;
  onDone: () => void;
  canRemove: boolean;
}

const SortableQuestionWrapper = ({
  question,
  index,
  groups,
  isEditing,
  onEdit,
  onUpdate,
  onRemove,
  onDone,
  canRemove,
}: SortableQuestionWrapperProps) => {
  const { t } = useTranslation("qualifications");
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex gap-2">
      <button
        type="button"
        aria-label={t("forms.editor.reorder")}
        className="mt-4 flex cursor-grab items-start text-muted-foreground hover:text-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <DotsSixVerticalIcon className="size-5" />
      </button>
      <div className="min-w-0 flex-1">
        {isEditing ? (
          <QuestionEditor
            question={question}
            index={index}
            groups={groups}
            onUpdate={onUpdate}
            onRemove={onRemove}
            onDone={onDone}
            canRemove={canRemove}
          />
        ) : (
          <QuestionCard
            question={question}
            index={index}
            onClick={onEdit}
            onRemove={onRemove}
            canRemove={canRemove}
          />
        )}
      </div>
    </div>
  );
};

interface QuestionEditorProps {
  question: Question;
  index: number;
  /** The groups already in the form, offered as the group field's choices. */
  groups: string[];
  onUpdate: (updates: Partial<Question>) => void;
  onRemove: () => void;
  onDone: () => void;
  canRemove: boolean;
}

const QuestionEditor = ({
  question,
  index,
  groups,
  onUpdate,
  onRemove,
  onDone,
  canRemove,
}: QuestionEditorProps) => {
  const { t } = useTranslation("qualifications");
  return (
    <div className="space-y-4 rounded-lg border bg-muted/40 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-3">
          <Field
            label={t("forms.editor.question", { index: index + 1 })}
            htmlFor={`q${question.id}-label`}
            required
          >
            <Input
              id={`q${question.id}-label`}
              value={question.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              placeholder={t("forms.editor.questionPlaceholder")}
              required
            />
          </Field>

          <Field
            label={t("forms.editor.questionDescription")}
            htmlFor={`q${question.id}-description`}
          >
            <Textarea
              id={`q${question.id}-description`}
              value={question.description || ""}
              onChange={(e) => onUpdate({ description: e.target.value })}
              placeholder={t("forms.editor.questionDescriptionPlaceholder")}
              rows={2}
            />
          </Field>

          <Field
            label={t("forms.editor.groups.field")}
            htmlFor={`q${question.id}-group`}
          >
            <Input
              id={`q${question.id}-group`}
              list={`q${question.id}-groups`}
              value={question.group || ""}
              onChange={(e) => onUpdate({ group: e.target.value })}
              placeholder={t("forms.editor.groups.placeholder")}
            />
            <datalist id={`q${question.id}-groups`}>
              {groups.map((group) => (
                <option key={group} value={group} />
              ))}
            </datalist>
          </Field>

          <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto_auto]">
            <Field
              label={t("forms.editor.type")}
              htmlFor={`q${question.id}-type`}
            >
              <Select
                value={question.type}
                onValueChange={(v) => onUpdate({ type: v as QuestionType })}
              >
                <SelectTrigger id={`q${question.id}-type`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["boolean", "select", "number", "text"] as const).map(
                    (type) => (
                      <SelectItem key={type} value={type}>
                        {t(`forms.editor.types.${type}`)}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label={t("forms.editor.scoringMode")}
              htmlFor={`q${question.id}-scoring-mode`}
              className="sm:w-40"
            >
              <Select
                value={question.scoring_mode}
                onValueChange={(v) =>
                  onUpdate({
                    scoring_mode: v as ScoringMode,
                  })
                }
              >
                <SelectTrigger id={`q${question.id}-scoring-mode`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weight">
                    {t("forms.editor.scoringModes.weight")}
                  </SelectItem>
                  <SelectItem value="minimum">
                    {t("forms.editor.scoringModes.minimum")}
                  </SelectItem>
                  <SelectItem value="none">
                    {t("forms.editor.scoringModes.none")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {question.scoring_mode === "weight" && (
              <Field
                label={t("forms.editor.weight")}
                htmlFor={`q${question.id}-weight`}
                className="sm:w-24"
              >
                <Input
                  id={`q${question.id}-weight`}
                  type="number"
                  step="0.1"
                  min="0"
                  // 0 is a weight (the question does not count), not "unset"
                  value={question.scoring.weight ?? 1}
                  onChange={(e) =>
                    onUpdate({
                      scoring: {
                        ...question.scoring,
                        weight:
                          e.target.value === ""
                            ? 0
                            : Math.max(0, Number(e.target.value) || 0),
                      },
                    })
                  }
                />
              </Field>
            )}
          </div>

          {question.scoring_mode !== "none" && (
            <>
              <Field
                label={t("forms.editor.noResponse")}
                htmlFor={`q${question.id}-no-response`}
              >
                <Select
                  value={
                    question.scoring.default === "ignore"
                      ? "ignore"
                      : String(question.scoring.default)
                  }
                  onValueChange={(v) =>
                    onUpdate({
                      scoring: {
                        ...question.scoring,
                        default: v === "ignore" ? "ignore" : Number(v),
                      },
                    })
                  }
                >
                  <SelectTrigger id={`q${question.id}-no-response`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ignore">
                      {t("forms.editor.noResponseOptions.ignore")}
                    </SelectItem>
                    <SelectItem value="0">
                      {t("forms.editor.noResponseOptions.zero")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {(question.type === "boolean" || question.type === "select") && (
                <ScoringValuesEditor question={question} onUpdate={onUpdate} />
              )}

              {question.type === "number" && (
                <RangesEditor question={question} onUpdate={onUpdate} />
              )}

              {question.type === "text" && (
                <TextScoringEditor question={question} onUpdate={onUpdate} />
              )}
            </>
          )}
        </div>

        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            confirm={t("forms.editor.removeQuestionConfirm")}
            aria-label={t("forms.editor.removeQuestion")}
            className="mt-6 text-danger hover:text-danger"
          >
            <TrashIcon className="size-4" />
          </Button>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          <CheckIcon className="size-4" />
          {t("forms.editor.done")}
        </Button>
      </div>
    </div>
  );
};

const ScoringValuesEditor = ({
  question,
  onUpdate,
}: {
  question: Question;
  onUpdate: (updates: Partial<Question>) => void;
}) => {
  const { t } = useTranslation("qualifications");
  const values = question.scoring.values || {};

  const updateValue = (key: string, score: string) => {
    const newValues = { ...values, [key]: Number(score) };
    onUpdate({ scoring: { ...question.scoring, values: newValues } });
  };

  const removeValue = (key: string) => {
    const newValues = { ...values };
    delete newValues[key];
    onUpdate({ scoring: { ...question.scoring, values: newValues } });
  };

  const addValue = () => {
    const newValues = { ...values, "": 0 };
    onUpdate({ scoring: { ...question.scoring, values: newValues } });
  };

  if (question.type === "boolean") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("forms.editor.scoreYes")}>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={values["true"] ?? 1}
            onChange={(e) => updateValue("true", e.target.value)}
          />
        </Field>
        <Field label={t("forms.editor.scoreNo")}>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={values["false"] ?? 0}
            onChange={(e) => updateValue("false", e.target.value)}
          />
        </Field>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>{t("forms.editor.options")}</Label>
      {Object.entries(values).map(([key, score]) => (
        <div key={key} className="flex gap-2">
          <Input
            placeholder={t("forms.editor.optionPlaceholder")}
            value={key}
            onChange={(e) => {
              const newValues = { ...values };
              delete newValues[key];
              newValues[e.target.value] = Number(score);
              onUpdate({ scoring: { ...question.scoring, values: newValues } });
            }}
          />
          <Input
            type="number"
            step="0.01"
            min="0"
            max="1"
            placeholder={t("forms.editor.scorePlaceholder")}
            value={Number(score)}
            onChange={(e) => updateValue(key, e.target.value)}
            className="w-24"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => removeValue(key)}
            aria-label={t("forms.editor.removeOption")}
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addValue}>
        <PlusIcon className="size-4" />
        {t("forms.editor.addOption")}
      </Button>
    </div>
  );
};

const RangesEditor = ({
  question,
  onUpdate,
}: {
  question: Question;
  onUpdate: (updates: Partial<Question>) => void;
}) => {
  const { t } = useTranslation("qualifications");
  const ranges = question.scoring.ranges || [];

  const addRange = () => {
    onUpdate({
      scoring: {
        ...question.scoring,
        ranges: [...ranges, { operator: "gte", threshold: 0, value: 1 }],
      },
    });
  };

  const updateRange = (index: number, updates: Partial<(typeof ranges)[0]>) => {
    const newRanges = [...ranges];
    newRanges[index] = { ...newRanges[index], ...updates };
    onUpdate({ scoring: { ...question.scoring, ranges: newRanges } });
  };

  const removeRange = (index: number) => {
    onUpdate({
      scoring: {
        ...question.scoring,
        ranges: ranges.filter((_item, i) => i !== index),
      },
    });
  };

  return (
    <div className="space-y-2">
      <Label>{t("forms.editor.ranges")}</Label>
      {ranges.map((range, index) => (
        <div key={index} className="flex gap-2">
          <Select
            value={range.operator}
            onValueChange={(v) =>
              updateRange(index, {
                operator: v as "gte" | "lte" | "gt" | "lt" | "eq",
              })
            }
          >
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gte">≥</SelectItem>
              <SelectItem value="lte">≤</SelectItem>
              <SelectItem value="gt">&gt;</SelectItem>
              <SelectItem value="lt">&lt;</SelectItem>
              <SelectItem value="eq">=</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="number"
            step="0.01"
            placeholder={t("forms.editor.thresholdValuePlaceholder")}
            value={range.threshold}
            onChange={(e) =>
              updateRange(index, { threshold: Number(e.target.value) })
            }
            className="w-28"
          />
          <span className="flex items-center px-2">→</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="1"
            placeholder={t("forms.editor.scorePlaceholder")}
            value={range.value}
            onChange={(e) =>
              updateRange(index, { value: Number(e.target.value) })
            }
            className="w-24"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => removeRange(index)}
            aria-label={t("forms.editor.removeRange")}
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addRange}>
        <PlusIcon className="size-4" />
        {t("forms.editor.addRange")}
      </Button>
    </div>
  );
};

const TextScoringEditor = ({
  question,
  onUpdate,
}: {
  question: Question;
  onUpdate: (updates: Partial<Question>) => void;
}) => {
  const { t } = useTranslation("qualifications");
  const scoringRange = question.scoring.scoring_range || { min: 0, max: 1 };
  const scoringLogic = question.scoring.scoring_logic || "";

  return (
    <div className="space-y-3">
      <Field label={t("forms.editor.scoringLogic")} required>
        <Textarea
          value={scoringLogic}
          onChange={(e) =>
            onUpdate({
              scoring: {
                ...question.scoring,
                scoring_logic: e.target.value,
              },
            })
          }
          placeholder={t("forms.editor.scoringLogicPlaceholder")}
          rows={3}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t("forms.editor.minScore")}>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={scoringRange.min}
            onChange={(e) =>
              onUpdate({
                scoring: {
                  ...question.scoring,
                  scoring_range: {
                    ...scoringRange,
                    min: Number(e.target.value),
                  },
                },
              })
            }
          />
        </Field>
        <Field label={t("forms.editor.maxScore")}>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={scoringRange.max}
            onChange={(e) =>
              onUpdate({
                scoring: {
                  ...question.scoring,
                  scoring_range: {
                    ...scoringRange,
                    max: Number(e.target.value),
                  },
                },
              })
            }
          />
        </Field>
      </div>
    </div>
  );
};
